// Estado y reglas del módulo Construcción. Se apoya en AppContext para todo lo
// que ya existe (proyectos/parques, CECO+Área, OC, despachos, inventario,
// usuarios, moneda) y guarda aparte sólo lo propio de la obra.
//
// Persistencia: localStorage con versión de esquema + migración (el ERP no tiene
// backend en producción; ver README del módulo en src/construction/README.md).
import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useAppContext } from './AppContext';
import { renewableStages } from '../data/renewablesZero';
import {
  buildConstructionAlerts,
  computeActualCost,
  computeEvm,
  materialReconciliation,
  milestoneStatus,
  paymentStatementAmounts,
  sCurve,
  toIsoDate
} from '../construction/evm';
import { makeConverter } from '../construction/money';
import { canDo } from '../construction/permissions';
import { buildConstructionDemo } from '../construction/seed';
import { t } from '../i18n';

const STORAGE_KEY = 'kota-construction';
export const CONSTRUCTION_SCHEMA_VERSION = 1;

export const COMMISSIONING_TEMPLATE = [
  'Inspección visual de strings y conectores',
  'Prueba de polaridad y tensión en circuito abierto (Voc)',
  'Prueba de aislamiento (megado) BT',
  'Pruebas de inversores y centros de transformación',
  'Pruebas de protecciones y relés MT',
  'Integración SCADA y comunicaciones',
  'Protocolo de energización aprobado (CEN/SEC)',
  'Pruebas de desempeño (PR) y disponibilidad',
  'Recepción provisional y traspaso a Operaciones'
];

const DEFAULT_SETTINGS = { activationStage: 'ntp', materialTolerancePct: 10 };

const emptyState = () => ({
  schemaVersion: CONSTRUCTION_SCHEMA_VERSION,
  settings: { ...DEFAULT_SETTINGS },
  projects: {},
  wbsItems: [],
  progressLogs: [],
  milestones: [],
  contractors: [],
  paymentStatements: [],
  dailyLogs: [],
  materialConsumptions: [],
  inspections: [],
  nonConformities: [],
  commissioning: {}
});

// Migración de esquema: hoy sólo existe la v1; completa colecciones faltantes
// para que versiones futuras puedan agregar campos sin romper datos guardados.
export const migrateConstructionState = (stored) => {
  const base = emptyState();
  if (!stored || typeof stored !== 'object') return base;
  const merged = { ...base, ...stored, settings: { ...DEFAULT_SETTINGS, ...(stored.settings || {}) } };
  Object.keys(base).forEach((key) => {
    if (Array.isArray(base[key]) && !Array.isArray(merged[key])) merged[key] = [];
    if (key === 'projects' || key === 'commissioning') {
      if (!merged[key] || typeof merged[key] !== 'object' || Array.isArray(merged[key])) merged[key] = {};
    }
  });
  merged.schemaVersion = CONSTRUCTION_SCHEMA_VERSION;
  return merged;
};

const loadState = () => {
  try {
    return migrateConstructionState(JSON.parse(window.localStorage.getItem(STORAGE_KEY) || 'null'));
  } catch {
    return emptyState();
  }
};

const newId = (prefix) => `${prefix}-${Date.now().toString(36)}${Math.random().toString(36).slice(2, 6)}`;
const todayIso = () => toIsoDate(new Date());
const nowStamp = () => {
  const date = new Date();
  return `${todayIso()} ${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}`;
};
const mergeById = (current = [], incoming = []) => {
  const map = new Map(current.map((item) => [item.id, item]));
  incoming.forEach((item) => map.set(item.id, { ...(map.get(item.id) || {}), ...item }));
  return [...map.values()];
};

const ConstructionContext = createContext(null);

export const ConstructionProvider = ({ children }) => {
  const {
    currentUser,
    parks,
    renewableProjects,
    projectBudgets,
    purchaseOrders,
    shipments,
    inventory,
    suppliers,
    users,
    preferences,
    pushNotification,
    addAuditRecord,
    importSeedData
  } = useAppContext();

  const [state, setState] = useState(loadState);

  useEffect(() => {
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
    } catch (error) {
      console.warn('No fue posible guardar datos de construcción.', error);
    }
  }, [state]);

  const role = currentUser?.role;
  const can = useCallback((action) => canDo(role, action), [role]);
  const guard = (action) => {
    if (!canDo(role, action)) throw new Error(t('No tienes permisos para esta acción.'));
  };

  const displayCurrency = preferences?.displayCurrency === 'CLP' ? 'CLP' : 'USD';
  const convert = useMemo(() => makeConverter(preferences?.fxUsdClp), [preferences?.fxUsdClp]);

  // ---------------------------------------------------------------- gate NTP
  const activationIndex = Math.max(0, renewableStages.indexOf(state.settings.activationStage));

  const projectGate = useCallback(
    (parkId) => {
      const park = (parks || []).find((candidate) => candidate.id === parkId) || null;
      const renewable = (renewableProjects || []).find((project) => project.parkId === parkId) || null;
      const stageIndex = renewable ? renewableStages.indexOf(renewable.stage) : -1;
      const constructionArea = (park?.areas || []).find((area) => /constru/i.test(area.name || '')) || null;
      return {
        park,
        renewable,
        stage: renewable?.stage || null,
        enabled: Boolean(park && renewable && stageIndex >= activationIndex),
        hasConstructionArea: Boolean(constructionArea && constructionArea.costCenterId),
        constructionArea,
        activatedAt: state.projects[parkId]?.activatedAt || null
      };
    },
    [parks, renewableProjects, activationIndex, state.projects]
  );

  // Al alcanzar el umbral (por defecto NTP) el proyecto pasa a "En construcción".
  useEffect(() => {
    const newlyEnabled = (parks || []).filter((park) => projectGate(park.id).enabled && !state.projects[park.id]?.activatedAt);
    if (newlyEnabled.length === 0) return;
    setState((prev) => {
      const projects = { ...prev.projects };
      newlyEnabled.forEach((park) => {
        const budget = (projectBudgets || []).find((entry) => entry.parkId === park.id);
        projects[park.id] = { currency: budget?.currency === 'CLP' ? 'CLP' : 'USD', ...(projects[park.id] || {}), activatedAt: todayIso() };
      });
      return { ...prev, projects };
    });
    newlyEnabled.forEach((park) => {
      pushNotification({
        title: t('Proyecto en construcción'),
        message: t('{0} alcanzó el umbral de activación y quedó habilitado en Construcción.', park.name),
        severity: 'info'
      });
    });
  }, [parks, projectGate, state.projects, projectBudgets, pushNotification]);

  // ------------------------------------------------------- cálculos por obra
  const projectCurrency = useCallback((parkId) => state.projects[parkId]?.currency || 'USD', [state.projects]);

  const summaries = useMemo(() => {
    const asOf = todayIso();
    const map = new Map();
    (parks || []).forEach((park) => {
      const gate = projectGate(park.id);
      if (!gate.enabled) return;
      const items = state.wbsItems.filter((item) => item.parkId === park.id);
      const logs = state.progressLogs.filter((log) => log.parkId === park.id);
      const currency = projectCurrency(park.id);
      const constructionAreas = (park.areas || []).filter((area) => /constru/i.test(area.name || ''));
      const actualCost = computeActualCost({
        items,
        parkId: park.id,
        areaIds: constructionAreas.map((area) => area.id),
        costCenterIds: constructionAreas.map((area) => area.costCenterId).filter(Boolean),
        purchaseOrders: purchaseOrders || [],
        contractors: state.contractors.filter((contract) => contract.parkId === park.id),
        paymentStatements: state.paymentStatements,
        convert,
        projectCurrency: currency
      });
      const evm = computeEvm({ items, logs, asOf, acByItem: actualCost.byItem, acUnassigned: actualCost.unassigned });
      const materials = materialReconciliation({
        items,
        logs,
        shipments: shipments || [],
        consumptions: state.materialConsumptions,
        inventory: inventory || [],
        parkId: park.id,
        asOf,
        tolerancePct: state.settings.materialTolerancePct
      });
      const milestones = state.milestones
        .filter((milestone) => milestone.parkId === park.id)
        .map((milestone) => ({ ...milestone, computedStatus: milestoneStatus(milestone) }));
      const alerts = items.length === 0
        ? []
        : buildConstructionAlerts({
            parkId: park.id,
            projectName: park.name,
            evm,
            milestones: state.milestones,
            nonConformities: state.nonConformities,
            materials
          });
      map.set(park.id, {
        parkId: park.id,
        parkName: park.name,
        currency,
        gate,
        items,
        logs,
        evm,
        actualCost,
        materials,
        milestones,
        alerts,
        curve: sCurve(items, logs, { asOf }),
        openNcs: state.nonConformities.filter((nc) => nc.parkId === park.id && nc.status !== 'cerrada').length,
        pendingProgress: logs.filter((log) => log.status === 'pendiente').length
      });
    });
    return map;
  }, [parks, projectGate, state, purchaseOrders, shipments, inventory, convert, projectCurrency]);

  const getSummary = useCallback((parkId) => summaries.get(parkId) || null, [summaries]);

  const constructionAlerts = useMemo(() => [...summaries.values()].flatMap((summary) => summary.alerts), [summaries]);

  // Agregado de portafolio en la moneda de visualización.
  const portfolio = useMemo(() => {
    const totals = { bac: 0, pv: 0, ev: 0, ac: 0 };
    let weightedProgress = 0;
    let weightedPlanned = 0;
    const withData = [...summaries.values()].filter((summary) => summary.items.length > 0);
    withData.forEach((summary) => {
      const p = summary.evm.project;
      const bac = convert(p.bac, summary.currency, displayCurrency);
      totals.bac += bac;
      totals.pv += convert(p.pv, summary.currency, displayCurrency);
      totals.ev += convert(p.ev, summary.currency, displayCurrency);
      totals.ac += convert(p.ac, summary.currency, displayCurrency);
      weightedProgress += bac * p.actualProgress;
      weightedPlanned += bac * p.plannedProgress;
    });
    return {
      projects: withData.length,
      ...totals,
      actualProgress: totals.bac > 0 ? weightedProgress / totals.bac : 0,
      plannedProgress: totals.bac > 0 ? weightedPlanned / totals.bac : 0,
      cpi: totals.ac > 0 ? totals.ev / totals.ac : null,
      spi: totals.pv > 0 ? totals.ev / totals.pv : null
    };
  }, [summaries, convert, displayCurrency]);

  // ------------------------------------------------------------------ acciones
  const update = (patch) => setState((prev) => ({ ...prev, ...(typeof patch === 'function' ? patch(prev) : patch) }));
  const userName = (userId) => (users || []).find((user) => user.id === userId)?.name || '—';

  const updateSettings = useCallback(
    (patch) => {
      if (!['super_user', 'project_director'].includes(role)) throw new Error(t('No tienes permisos para esta acción.'));
      update((prev) => ({ settings: { ...prev.settings, ...patch } }));
      addAuditRecord('Configuración construcción', 'construction', JSON.stringify(patch));
    },
    [role, addAuditRecord]
  );

  const setProjectCurrency = (parkId, currency) => {
    guard('costs.editBudget');
    update((prev) => ({ projects: { ...prev.projects, [parkId]: { ...(prev.projects[parkId] || {}), currency: currency === 'CLP' ? 'CLP' : 'USD' } } }));
  };

  const upsertWbsItem = (payload) => {
    guard('schedule.edit');
    const name = String(payload.name || '').trim();
    if (!payload.parkId) throw new Error(t('Proyecto inválido.'));
    if (!name) throw new Error(t('Ingresa el nombre de la partida.'));
    if (payload.baselineStart && payload.baselineEnd && payload.baselineEnd < payload.baselineStart) {
      throw new Error(t('La fecha de término no puede ser anterior al inicio.'));
    }
    const item = {
      predecessors: [],
      materials: [],
      status: 'auto',
      ...payload,
      id: payload.id || newId('wbs'),
      name,
      weight: payload.weight === '' || payload.weight == null ? undefined : Number(payload.weight),
      bac: payload.bac === '' || payload.bac == null ? undefined : Number(payload.bac)
    };
    update((prev) => ({ wbsItems: mergeById(prev.wbsItems, [item]) }));
    addAuditRecord(payload.id ? 'Edición partida obra' : 'Alta partida obra', item.id, item.name);
    return item;
  };

  const deleteWbsItem = (itemId) => {
    guard('schedule.edit');
    update((prev) => {
      const toDelete = new Set([itemId]);
      let changed = true;
      while (changed) {
        changed = false;
        prev.wbsItems.forEach((item) => {
          if (item.parentId && toDelete.has(item.parentId) && !toDelete.has(item.id)) {
            toDelete.add(item.id);
            changed = true;
          }
        });
      }
      return {
        wbsItems: prev.wbsItems.filter((item) => !toDelete.has(item.id)),
        progressLogs: prev.progressLogs.filter((log) => !toDelete.has(log.wbsItemId))
      };
    });
    addAuditRecord('Eliminación partida obra', itemId, 'OK');
  };

  const registerProgress = ({ parkId, wbsItemId, date, progress, note }) => {
    guard('progress.register');
    const value = Number(progress);
    if (!wbsItemId) throw new Error(t('Selecciona una partida.'));
    if (!Number.isFinite(value) || value < 0 || value > 100) throw new Error(t('El avance debe estar entre 0 y 100.'));
    const autoApprove = canDo(role, 'progress.approve');
    const entry = {
      id: newId('prog'),
      parkId,
      wbsItemId,
      date: date || todayIso(),
      progress: value,
      note: note || '',
      userId: currentUser?.id,
      status: autoApprove ? 'aprobado' : 'pendiente',
      approvedBy: autoApprove ? currentUser?.id : null,
      createdAt: nowStamp()
    };
    update((prev) => ({ progressLogs: [entry, ...prev.progressLogs] }));
    addAuditRecord('Registro avance obra', wbsItemId, `${value}%`);
    if (!autoApprove) {
      pushNotification({ title: t('Avance pendiente de aprobación'), message: t('Se registró un avance de {0}% que requiere aprobación del Jefe de Proyecto.', value), severity: 'medium' });
    }
    return entry;
  };

  const reviewProgress = (logId, approve) => {
    guard('progress.approve');
    update((prev) => ({
      progressLogs: prev.progressLogs.map((log) =>
        log.id === logId ? { ...log, status: approve ? 'aprobado' : 'rechazado', approvedBy: currentUser?.id } : log
      )
    }));
    addAuditRecord(approve ? 'Aprobación avance obra' : 'Rechazo avance obra', logId, approve ? 'Aprobado' : 'Rechazado');
  };

  const upsertMilestone = (payload) => {
    guard('schedule.edit');
    if (!String(payload.name || '').trim()) throw new Error(t('Ingresa el nombre del hito.'));
    if (!payload.targetDate) throw new Error(t('Ingresa la fecha objetivo.'));
    const milestone = { completedDate: null, wbsItemId: null, ...payload, id: payload.id || newId('ms') };
    update((prev) => ({ milestones: mergeById(prev.milestones, [milestone]) }));
    addAuditRecord('Hito de obra', milestone.id, milestone.name);
  };

  const completeMilestone = (milestoneId, completed = true) => {
    guard('schedule.edit');
    update((prev) => ({
      milestones: prev.milestones.map((milestone) =>
        milestone.id === milestoneId ? { ...milestone, completedDate: completed ? todayIso() : null } : milestone
      )
    }));
  };

  const addDailyLog = (payload) => {
    guard('daily.edit');
    if (!payload.parkId) throw new Error(t('Proyecto inválido.'));
    if (!String(payload.activities || '').trim()) throw new Error(t('Describe las actividades del día.'));
    const entry = {
      crews: [],
      attachments: [],
      ...payload,
      id: newId('pd'),
      date: payload.date || todayIso(),
      userId: currentUser?.id,
      createdAt: nowStamp()
    };
    update((prev) => ({ dailyLogs: [entry, ...prev.dailyLogs] }));
    addAuditRecord('Parte diario', entry.id, entry.date);
    return entry;
  };

  const addConsumption = (payload) => {
    guard('materials.edit');
    const quantity = Number(payload.quantity);
    if (!payload.sku) throw new Error(t('Selecciona un material.'));
    if (!Number.isFinite(quantity) || quantity <= 0) throw new Error(t('La cantidad debe ser mayor a 0.'));
    const entry = { ...payload, quantity, id: newId('cons'), date: payload.date || todayIso(), userId: currentUser?.id };
    update((prev) => ({ materialConsumptions: [entry, ...prev.materialConsumptions] }));
    addAuditRecord('Consumo material en obra', payload.sku, String(quantity));
    return entry;
  };

  const upsertContractor = (payload) => {
    guard('contracts.edit');
    const supplier = (suppliers || []).find((candidate) => candidate.id === payload.supplierId);
    const name = String(payload.name || supplier?.name || '').trim();
    if (!name) throw new Error(t('Selecciona un proveedor o ingresa el nombre del contratista.'));
    const amount = Number(payload.contractAmount);
    if (!Number.isFinite(amount) || amount <= 0) throw new Error(t('Ingresa el monto del contrato.'));
    const contract = {
      wbsItemIds: [],
      retentionPct: 5,
      status: 'vigente',
      ...payload,
      id: payload.id || newId('ctr'),
      name,
      contractAmount: amount,
      retentionPct: Number(payload.retentionPct ?? 5),
      createdBy: payload.createdBy || currentUser?.id
    };
    update((prev) => ({ contractors: mergeById(prev.contractors, [contract]) }));
    addAuditRecord(payload.id ? 'Edición contrato obra' : 'Alta contrato obra', contract.id, contract.name);
    return contract;
  };

  const createPaymentStatement = ({ contractorId, periodEnd, progressPct, note }) => {
    guard('payments.create');
    const contract = state.contractors.find((candidate) => candidate.id === contractorId);
    if (!contract) throw new Error(t('Selecciona un contrato.'));
    const statements = state.paymentStatements.filter((statement) => statement.contractorId === contractorId && statement.status !== 'rechazado');
    const previousPct = statements.reduce((max, statement) => Math.max(max, Number(statement.progressPct) || 0), 0);
    const pct = Number(progressPct);
    if (!Number.isFinite(pct) || pct <= previousPct || pct > 100) {
      throw new Error(t('El avance acumulado debe ser mayor al ya valorizado ({0}%) y como máximo 100%.', previousPct));
    }
    const amounts = paymentStatementAmounts(contract.contractAmount, pct, previousPct, contract.retentionPct);
    const statement = {
      id: newId('ep'),
      parkId: contract.parkId,
      contractorId,
      number: statements.length + 1,
      periodEnd: periodEnd || todayIso(),
      progressPct: pct,
      previousPct,
      ...amounts,
      status: 'pendiente_pm',
      createdBy: currentUser?.id,
      history: [{ status: 'pendiente_pm', userId: currentUser?.id, at: nowStamp(), note: note || '' }]
    };
    update((prev) => ({ paymentStatements: [statement, ...prev.paymentStatements] }));
    addAuditRecord('Emisión estado de pago', statement.id, `${contract.name} EP ${statement.number}`);
    pushNotification({ title: t('Estado de pago emitido'), message: t('EP {0} de {1} espera aprobación del Jefe de Proyecto.', statement.number, contract.name), severity: 'medium' });
    return statement;
  };

  // Flujo análogo a "Flujo aprobación" de Abastecimiento:
  // pendiente_pm → pendiente_director → aprobado → pagado (o rechazado).
  const advancePaymentStatement = (statementId, decision, note = '') => {
    const statement = state.paymentStatements.find((candidate) => candidate.id === statementId);
    if (!statement) return;
    let next = null;
    if (decision === 'reject') {
      guard(statement.status === 'pendiente_pm' ? 'payments.approvePm' : 'payments.approveDirector');
      next = 'rechazado';
    } else if (statement.status === 'pendiente_pm') {
      guard('payments.approvePm');
      next = 'pendiente_director';
    } else if (statement.status === 'pendiente_director') {
      guard('payments.approveDirector');
      next = 'aprobado';
    } else if (statement.status === 'aprobado') {
      guard('payments.markPaid');
      next = 'pagado';
    }
    if (!next) return;
    update((prev) => ({
      paymentStatements: prev.paymentStatements.map((candidate) =>
        candidate.id === statementId
          ? { ...candidate, status: next, history: [...(candidate.history || []), { status: next, userId: currentUser?.id, at: nowStamp(), note }] }
          : candidate
      )
    }));
    addAuditRecord('Estado de pago', statementId, next);
  };

  const addInspection = (payload) => {
    guard('quality.edit');
    if (!payload.parkId) throw new Error(t('Proyecto inválido.'));
    const checklist = (payload.checklist || []).filter((entry) => String(entry.label || '').trim());
    if (checklist.length === 0) throw new Error(t('Agrega al menos un punto al checklist.'));
    const failed = checklist.some((entry) => entry.ok === false);
    const inspection = {
      ...payload,
      checklist,
      id: newId('insp'),
      date: payload.date || todayIso(),
      inspectorId: currentUser?.id,
      result: failed ? 'observado' : 'conforme'
    };
    update((prev) => ({ inspections: [inspection, ...prev.inspections] }));
    addAuditRecord('Inspección de obra', inspection.id, inspection.result);
    return inspection;
  };

  const addNonConformity = (payload) => {
    guard('quality.edit');
    if (!String(payload.title || '').trim()) throw new Error(t('Ingresa el título de la no conformidad.'));
    const nc = { severity: 'media', ...payload, id: newId('nc'), openedAt: todayIso(), status: 'abierta', closedAt: null, closureNote: '' };
    update((prev) => ({ nonConformities: [nc, ...prev.nonConformities] }));
    addAuditRecord('Apertura NC', nc.id, nc.title);
    return nc;
  };

  const updateNonConformityStatus = (ncId, status, closureNote = '') => {
    guard(status === 'cerrada' ? 'quality.closeNc' : 'quality.edit');
    update((prev) => ({
      nonConformities: prev.nonConformities.map((nc) =>
        nc.id === ncId ? { ...nc, status, closedAt: status === 'cerrada' ? todayIso() : null, closureNote: closureNote || nc.closureNote } : nc
      )
    }));
    addAuditRecord('Estado NC', ncId, status);
  };

  const getCommissioning = useCallback(
    (parkId) => {
      const saved = state.commissioning[parkId] || [];
      return COMMISSIONING_TEMPLATE.map((label, index) => {
        const id = `cx-${index + 1}`;
        return { id, label, ...(saved.find((entry) => entry.id === id) || { done: false, doneAt: null, doneBy: null }) };
      });
    },
    [state.commissioning]
  );

  const toggleCommissioningItem = (parkId, itemId) => {
    guard('commissioning.edit');
    update((prev) => {
      const current = prev.commissioning[parkId] || [];
      const existing = current.find((entry) => entry.id === itemId);
      const nextEntry = existing?.done
        ? { id: itemId, done: false, doneAt: null, doneBy: null }
        : { id: itemId, done: true, doneAt: todayIso(), doneBy: currentUser?.id };
      return { commissioning: { ...prev.commissioning, [parkId]: [...current.filter((entry) => entry.id !== itemId), nextEntry] } };
    });
  };

  const loadDemo = () => {
    if (!['super_user', 'project_director'].includes(role)) throw new Error(t('No tienes permisos para esta acción.'));
    const demo = buildConstructionDemo(new Date());
    importSeedData(demo.base);
    update((prev) => {
      const next = { ...prev, projects: { ...prev.projects, ...demo.construction.projects } };
      Object.entries(demo.construction).forEach(([key, value]) => {
        if (Array.isArray(value)) next[key] = mergeById(prev[key], value);
      });
      return next;
    });
    pushNotification({ title: t('Demo de Construcción cargada'), message: t('Se cargó el proyecto Parque Solar Valle Sur en NTP con datos de obra.'), severity: 'info' });
  };

  const resetConstruction = useCallback(() => setState(emptyState()), []);

  const value = {
    state,
    settings: state.settings,
    can,
    displayCurrency,
    convert,
    userName,
    projectGate,
    projectCurrency,
    getSummary,
    summaries,
    constructionAlerts,
    portfolio,
    getCommissioning,
    updateSettings,
    setProjectCurrency,
    upsertWbsItem,
    deleteWbsItem,
    registerProgress,
    reviewProgress,
    upsertMilestone,
    completeMilestone,
    addDailyLog,
    addConsumption,
    upsertContractor,
    createPaymentStatement,
    advancePaymentStatement,
    addInspection,
    addNonConformity,
    updateNonConformityStatus,
    toggleCommissioningItem,
    loadDemo,
    resetConstruction
  };

  return <ConstructionContext.Provider value={value}>{children}</ConstructionContext.Provider>;
};

export const useConstruction = () => {
  const context = useContext(ConstructionContext);
  if (!context) throw new Error('useConstruction debe utilizarse dentro de ConstructionProvider');
  return context;
};
